import { ArrowRight, HelpCircle, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { getPageHelp, type PageHelpId } from './page-help-content';

interface PageHelpProps {
  id: PageHelpId;
}

/**
 * Botão "?" que abre um painel flutuante com explicação humana da página.
 * Conteúdo centralizado em `page-help-content.tsx` pra edição fácil.
 */
export function PageHelp({ id }: PageHelpProps): JSX.Element | null {
  const content = getPageHelp(id);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click fora fecha
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // ESC fecha
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (!content) return null;

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        type="button"
        aria-label="Ajuda desta página"
        aria-expanded={open}
        title="O que é esta página?"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-line bg-panel-2 text-muted transition-colors hover:bg-panel-3 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-good/40"
      >
        <HelpCircle size={14} strokeWidth={1.8} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={content.title}
          className="absolute right-0 top-full z-50 mt-2 w-[min(380px,calc(100vw-32px))] rounded-[12px] border border-line bg-panel p-4 shadow-xl"
        >
          <div className="mb-3 flex items-start justify-between gap-2">
            <h3 className="m-0 text-[14px] font-semibold tracking-[-0.005em] text-ink">
              {content.title}
            </h3>
            <button
              type="button"
              aria-label="Fechar"
              onClick={() => setOpen(false)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] text-muted hover:bg-panel-2 hover:text-ink"
            >
              <X size={14} strokeWidth={1.8} />
            </button>
          </div>

          <div className="space-y-3 text-[12.5px] leading-relaxed text-ink-2">
            <p className="m-0">{content.what}</p>
            {content.how && (
              <div>
                <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-2">
                  Como funciona
                </div>
                <p className="m-0 text-ink-2">{content.how}</p>
              </div>
            )}
            {content.related && content.related.length > 0 && (
              <div className="border-t border-line pt-3">
                <div className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-2">
                  Páginas relacionadas
                </div>
                <ul className="m-0 space-y-1 p-0">
                  {content.related.map((r) => (
                    <li key={r.to} className="list-none">
                      <Link
                        to={r.to}
                        onClick={() => setOpen(false)}
                        className="inline-flex items-center gap-1 text-[12px] text-good hover:underline"
                      >
                        {r.label}
                        <ArrowRight size={11} strokeWidth={2} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
